import { useEffect, useRef, useState } from 'preact/hooks';
import { Send, Square, Sparkles, ArrowDown, Paperclip, CornerUpLeft, X as XIcon } from 'lucide-preact';
import { PageHeader } from '@/components/PageHeader';
import { PageState } from '@/components/PageState';
import { StatusDot } from '@/components/Pill';
import { useFetch } from '@/lib/useFetch';
import { apiGet, apiPost, apiUpload, chatId } from '@/lib/api';
import { renderMarkdown } from '@/lib/markdown';
import { formatCost, formatNumber } from '@/lib/format';
import { showCosts } from '@/lib/theme';
import { subscribeChatStream, chatStreamConnected, resetUnread } from '@/lib/chat-stream';

interface Turn { role: 'user' | 'assistant'; content: string; source?: string; agent_id?: string; created_at?: number; photoUrl?: string; photoCaption?: string; }
interface Agent { id: string; name: string; running: boolean; }
interface PeerIdentity { id: string; name: string; }
interface PeerMessage { id: number; from_id: string; from_name: string; to_id: string; content: string; created_at: number; }

interface AgentTokens { todayCost: number; todayTurns: number; allTimeCost: number; }
interface Health { contextPct: number; turns: number; model: string; }

const QUICK_ACTIONS = [
  { label: 'Status update', prompt: "Quick status update: what are you working on right now?" },
  { label: "What's next", prompt: 'What should I focus on next based on context?' },
  { label: 'Plan today', prompt: 'What does my day look like today? What are the priorities?' },
  { label: 'Recent wins', prompt: 'What did I accomplish in the last 24 hours?' },
];

export function Chat() {
  const agents = useFetch<{ agents: Agent[] }>('/api/agents', 60_000);
  const peers = useFetch<{ identities: PeerIdentity[] }>('/api/peer/identities', 60_000);
  // activeAgent values: 'all' | <agentId> | `peer:<identityId>` for DM tabs.
  const [activeAgent, setActiveAgent] = useState<string>('all');
  const [turns, setTurns] = useState<Turn[]>([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progressLabel, setProgressLabel] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const streamConnected = chatStreamConnected.value;
  // Track whether the message list is scrolled near the bottom. Drives
  // the floating "scroll to latest" button and tells the auto-scroll
  // effect whether it's safe to jump on a new turn (we don't yank the
  // viewport while the user is reading older messages).
  const [atBottom, setAtBottom] = useState(true);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Click a bot's bubble in the All chat → reply is routed to THAT specific
  // agent (dispatched as a mission task with the quoted snippet for context).
  // null = no active reply target; the textarea sends to main as usual.
  const [replyingTo, setReplyingTo] = useState<{ agentId: string; snippet: string } | null>(null);

  function clearReply() { setReplyingTo(null); }
  function startReply(agentId: string, content: string) {
    setReplyingTo({ agentId, snippet: content.slice(0, 240) });
    // Focus the input so the user can type immediately
    requestAnimationFrame(() => inputRef.current?.focus());
  }

  async function uploadImage(file: File, caption?: string) {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setError('Only image files supported.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Image too large (max 10 MB).');
      return;
    }
    setUploadingImage(true); setError(null);
    try {
      const form = new FormData();
      form.append('image', file);
      if (caption) form.append('caption', caption);
      await apiUpload<{ ok: boolean; url: string }>('/api/chat/upload-image', form);
      // SSE user_photo event will append the bubble; no optimistic insert.
    } catch (e: any) {
      setError(e?.message || 'Upload failed.');
    } finally {
      setUploadingImage(false);
    }
  }

  function onPasteImage(e: ClipboardEvent) {
    if (activeAgent !== 'all' && !activeAgent.startsWith('main')) return; // dashboard chat only
    const items = e.clipboardData?.items;
    if (!items) return;
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.type.startsWith('image/')) {
        const file = it.getAsFile();
        if (file) {
          e.preventDefault();
          void uploadImage(file, draft.trim() || undefined);
          setDraft('');
          return;
        }
      }
    }
  }
  // Common emoji set — covers reactions, business comms, real-estate context
  const EMOJI_GRID = ['👍','❤️','😂','🙏','✅','❌','🎯','🔥','💯','🚀','📞','📧','💬','🤔','👀','⏰','💰','🏠','🏞️','🤝','😅','😊','📍','📝','⚠️','🛑','✨','🙌','😎','👋'];

  function insertEmoji(e: string) {
    const ta = inputRef.current;
    if (!ta) {
      setDraft((d) => d + e);
      return;
    }
    const start = ta.selectionStart ?? draft.length;
    const end = ta.selectionEnd ?? draft.length;
    const next = draft.slice(0, start) + e + draft.slice(end);
    setDraft(next);
    // Restore cursor position after the inserted emoji
    requestAnimationFrame(() => {
      if (!inputRef.current) return;
      const pos = start + e.length;
      inputRef.current.focus();
      inputRef.current.setSelectionRange(pos, pos);
    });
  }

  function scrollToBottom(smooth = true) {
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }

  // Live session info for the bar.
  const health = useFetch<Health>(`/api/health?chatId=${encodeURIComponent(chatId)}`, 30_000);
  const agentTokens = useFetch<AgentTokens>(
    activeAgent === 'all' ? null : `/api/agents/${activeAgent}/tokens`,
    30_000,
  );

  // Load conversation history when active agent changes.
  // Also poll every 5s as a backstop: SSE only broadcasts events from
  // the main daemon's process. Sub-agents (research, comms, etc.) run as
  // separate processes — their Telegram exchanges land in the shared DB
  // but never hit our SSE stream. Without polling, those messages would
  // only appear on a manual page refresh.
  useEffect(() => {
    let cancelled = false;
    function fetchHistory(initial: boolean) {
      if (initial) setLoading(true);
      // Peer DM tab: query peer endpoint, project each message as a Turn so
      // it slots into the existing render path. Sender identity drives role:
      // messages I sent render as 'user' (right-aligned); messages they sent
      // render as 'assistant' (left-aligned).
      if (activeAgent.startsWith('peer:')) {
        const withId = activeAgent.slice(5);
        apiGet<{ messages: PeerMessage[] }>(`/api/peer/conversation?with=${encodeURIComponent(withId)}&limit=200`)
          .then((d) => {
            if (cancelled) return;
            const next: Turn[] = (d.messages || []).map((m) => ({
              role: m.to_id === withId ? 'user' : 'assistant',
              content: m.content,
              source: 'peer',
              created_at: m.created_at,
            }));
            setTurns(next);
          })
          .catch((e) => { if (!cancelled && initial) setError(e?.message || String(e)); })
          .finally(() => { if (!cancelled && initial) setLoading(false); });
        return;
      }

      const path = activeAgent === 'all'
        ? `/api/chat/history?chatId=${encodeURIComponent(chatId)}&limit=200`
        : `/api/agents/${activeAgent}/conversation?chatId=${encodeURIComponent(chatId)}&limit=200`;
      apiGet<{ turns: Turn[] }>(path)
        .then((d) => {
          if (cancelled) return;
          // API returns DESC (newest first), but a chat UI puts newest at the
          // BOTTOM. Reverse so the array is chronological top→bottom: oldest
          // first, newest last. SSE appends new messages at the end already,
          // so this aligns the two flows.
          const next = (d.turns || []).slice().reverse();
          setTurns((prev) => {
            // Skip the state update if nothing changed to avoid re-render flicker.
            if (prev.length === next.length && prev.length > 0
                && prev[prev.length - 1]?.content === next[next.length - 1]?.content
                && prev[0]?.content === next[0]?.content) {
              return prev;
            }
            return next;
          });
        })
        .catch((e) => { if (!cancelled && initial) setError(e?.message || String(e)); })
        .finally(() => { if (!cancelled && initial) setLoading(false); });
    }
    fetchHistory(true);
    const pollId = setInterval(() => fetchHistory(false), 5000);
    return () => { cancelled = true; clearInterval(pollId); };
  }, [activeAgent]);

  // Auto-scroll only when the user is already near the bottom. New
  // messages arriving while they're reading history shouldn't yank
  // them away.
  useEffect(() => {
    if (atBottom && messagesRef.current) {
      messagesRef.current.scrollTop = messagesRef.current.scrollHeight;
    }
  }, [turns, processing, atBottom]);

  // Watch the message list scroll position so the "scroll to latest"
  // button shows up the moment the user scrolls away from the bottom.
  useEffect(() => {
    const el = messagesRef.current;
    if (!el) return;
    function onScroll() {
      const dist = el!.scrollHeight - el!.scrollTop - el!.clientHeight;
      setAtBottom(dist < 60);
    }
    el.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => el.removeEventListener('scroll', onScroll);
  }, [activeAgent]);

  // Subscribe to the global chat SSE (started in main.tsx). The page just
  // reads the events; the stream itself stays open for the whole app
  // lifecycle so the sidebar unread badge keeps tracking.
  useEffect(() => {
    resetUnread();
    const unsub = subscribeChatStream((eventName, data) => {
      if (eventName === 'reconnected') {
        // SSE dropped and came back (network blip, tab regained focus,
        // laptop woke). Anything that landed in the DB while we were
        // disconnected won't be in `turns`. Reload history so we catch up.
        const path = activeAgent === 'all'
          ? `/api/chat/history?chatId=${encodeURIComponent(chatId)}&limit=200`
          : `/api/agents/${activeAgent}/conversation?chatId=${encodeURIComponent(chatId)}&limit=200`;
        apiGet<{ turns: Turn[] }>(path).then((d) => setTurns((d.turns || []).slice().reverse())).catch(() => {});
      } else if (eventName === 'user_message') {
        setTurns((prev) => [...prev, { role: 'user', content: data.content, source: data.source, created_at: Date.now() }]);
      } else if (eventName === 'assistant_message') {
        setTurns((prev) => [...prev, { role: 'assistant', content: data.content, source: data.source, created_at: Date.now() }]);
        setProcessing(false); setProgressLabel(null);
        health.refresh();
        if (activeAgent !== 'all') agentTokens.refresh();
      } else if (eventName === 'assistant_photo') {
        // Inline photo bubble. The bot already stripped the marker from
        // the text-side assistant_message; this event carries the URL.
        setTurns((prev) => [...prev, {
          role: 'assistant',
          content: '',
          source: data.source,
          photoUrl: data.url,
          photoCaption: data.caption,
          created_at: Date.now(),
        }]);
      } else if (eventName === 'user_photo') {
        // Image uploaded from the dashboard (Dwight or Nichole attached a
        // screenshot). Render as a right-aligned user bubble.
        setTurns((prev) => [...prev, {
          role: 'user',
          content: '',
          source: data.source,
          photoUrl: data.url,
          photoCaption: data.caption,
          created_at: Date.now(),
        }]);
      } else if (eventName === 'processing') {
        if (data.processing !== undefined) setProcessing(!!data.processing);
        if (!data.processing) setProgressLabel(null);
      } else if (eventName === 'progress') {
        if (data.description) setProgressLabel(data.description);
      } else if (eventName === 'error') {
        setTurns((prev) => [...prev, { role: 'assistant', content: data.content || 'Error', created_at: Date.now() }]);
        setProcessing(false); setProgressLabel(null);
      } else if (eventName === 'peer_message') {
        // Live DM from another human. Only render if I'm on that DM tab AND
        // I am the recipient (avoid double-rendering my own optimistic insert).
        if (!activeAgent.startsWith('peer:')) return;
        const peerId = activeAgent.slice(5);
        const fromId = data.peerFromId;
        const toId = data.peerToId;
        // I should see this message if I'm in the DM with the sender and I'm
        // the recipient. (My own outgoing message already rendered optimistically.)
        if (fromId === peerId && toId !== peerId) {
          setTurns((prev) => [...prev, { role: 'assistant', content: data.content, source: 'peer', created_at: Date.now() }]);
        }
      }
    });
    return unsub;
  }, [activeAgent]);

  async function send(textOverride?: string) {
    const message = (textOverride ?? draft).trim();
    if (!message) return;
    setSending(true); setError(null);
    try {
      // Peer DM tab: route to /api/peer/send with the recipient identity.
      if (activeAgent.startsWith('peer:')) {
        const toId = activeAgent.slice(5);
        const res = await apiPost<{ ok?: boolean; error?: string }>('/api/peer/send', { to: toId, message });
        if (!res.ok && res.error) {
          setError(res.error);
        } else {
          if (!textOverride) setDraft('');
          // Optimistic append — SSE will arrive too but we render right away.
          setTurns((prev) => [...prev, { role: 'user', content: message, source: 'peer', created_at: Date.now() }]);
        }
        return;
      }

      // Targeted reply path: if the user clicked a specific bot's bubble first,
      // route this message to THAT agent (via mission task) instead of main.
      if (replyingTo) {
        const target = replyingTo;
        const res = await apiPost<{ ok?: boolean; error?: string; missionId?: string }>(
          '/api/chat/reply-to-agent',
          { agentId: target.agentId, message, quotedSnippet: target.snippet },
        );
        if (!res.ok && res.error) {
          setError(res.error);
        } else {
          if (!textOverride) setDraft('');
          clearReply();
        }
        return;
      }

      const res = await apiPost<{ ok?: boolean; error?: string }>('/api/chat/send', { message });
      if (!res.ok && res.error) {
        setError(res.error === 'busy' ? 'A turn is already in flight. Wait for it to finish.' : res.error);
      } else if (!textOverride) {
        setDraft('');
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally { setSending(false); }
  }

  // Broadcast: only available on the All tab. Sends one message to every
  // sub-agent at once via the mission queue. Each replies in its own thread;
  // the All tab shows them all together as the polling cycles pick them up.
  async function broadcast() {
    const message = draft.trim();
    if (!message) return;
    setSending(true); setError(null);
    try {
      const res = await apiPost<{ ok?: boolean; error?: string; dispatched?: string[]; count?: number }>(
        '/api/chat/broadcast', { message },
      );
      if (!res.ok && res.error) {
        setError(res.error);
      } else {
        setDraft('');
      }
    } catch (err: any) {
      setError(err?.message || String(err));
    } finally { setSending(false); }
  }

  async function abort() {
    try { await apiPost('/api/chat/abort'); } catch {}
  }

  function quick(prompt: string) {
    void send(prompt);
    inputRef.current?.focus();
  }

  const agentList = agents.data?.agents ?? [];
  const activeAgentObj = agentList.find((a) => a.id === activeAgent);
  const todayCost = activeAgent === 'all'
    ? agentList.reduce((sum, a: any) => sum + (a.todayCost || 0), 0)
    : agentTokens.data?.todayCost ?? 0;
  const todayTurns = activeAgent === 'all'
    ? agentList.reduce((sum, a: any) => sum + (a.todayTurns || 0), 0)
    : agentTokens.data?.todayTurns ?? 0;

  return (
    <div class="flex flex-col h-full">
      <PageHeader
        title="Chat"
        actions={
          <span class="inline-flex items-center gap-1.5 text-[11px] text-[var(--color-text-muted)]">
            <StatusDot tone={streamConnected ? 'done' : 'cancelled'} />
            {streamConnected ? 'Stream live' : 'Reconnecting…'}
          </span>
        }
        tabs={
          <>
            <TabBtn label="All" active={activeAgent === 'all'} onClick={() => setActiveAgent('all')} />
            {agentList.map((a) => (
              <TabBtn key={a.id} label={a.name || a.id} active={activeAgent === a.id} onClick={() => setActiveAgent(a.id)} live={a.running} />
            ))}
            {/* DM tabs: one per human peer the current user can DM. */}
            {(peers.data?.identities || []).map((p) => (
              <TabBtn
                key={`peer:${p.id}`}
                label={`💬 ${p.name}`}
                active={activeAgent === `peer:${p.id}`}
                onClick={() => setActiveAgent(`peer:${p.id}`)}
              />
            ))}
          </>
        }
      />

      <SessionBar
        contextPct={health.data?.contextPct}
        turnsToday={todayTurns}
        costToday={todayCost}
        model={activeAgent === 'all' ? health.data?.model : undefined}
        agentLabel={activeAgentObj ? activeAgentObj.name || activeAgentObj.id : undefined}
      />

      <div class="relative flex-1 min-h-0">
        <div ref={messagesRef} class="absolute inset-0 overflow-y-auto px-6 py-4 space-y-2">
          {error && <div class="text-[var(--color-status-failed)] text-[11.5px]">{error}</div>}
          {loading && <PageState loading />}
          {!loading && turns.length === 0 && (
            <PageState empty emptyTitle="No messages yet" emptyDescription="Type below to talk to your agent. Replies stream in via SSE." />
          )}
          {turns.map((t, i) => <Bubble key={i} turn={t} onReply={activeAgent === 'all' && !activeAgent.startsWith('peer:') ? startReply : undefined} />)}
          {processing && <ProcessingBubble label={progressLabel} />}
        </div>
        {!atBottom && (
          <button
            type="button"
            onClick={() => scrollToBottom(true)}
            class="absolute bottom-3 right-4 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11.5px] font-medium bg-[var(--color-accent)] text-white shadow-lg hover:bg-[var(--color-accent-hover)] transition-colors"
            aria-label="Scroll to latest message"
          >
            <ArrowDown size={13} />
            Latest
          </button>
        )}
      </div>

      <div class="border-t border-[var(--color-border)] px-4 pt-2 pb-3">
        <div class="max-w-4xl mx-auto">
          <div class="flex items-center gap-1 mb-2 flex-wrap">
            {QUICK_ACTIONS.map((qa) => (
              <button
                key={qa.label}
                type="button"
                onClick={() => quick(qa.prompt)}
                disabled={processing || sending}
                class="px-2 py-0.5 rounded text-[10.5px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)] border border-[var(--color-border)] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {qa.label}
              </button>
            ))}
          </div>
          {replyingTo && (
            <div class="mb-2 flex items-start gap-2 px-2.5 py-1.5 rounded bg-[var(--color-elevated)] border-l-2 border-[var(--color-accent)] text-[11px]">
              <CornerUpLeft size={12} class="text-[var(--color-accent)] mt-0.5 shrink-0" />
              <div class="flex-1 min-w-0">
                <div class="font-medium text-[var(--color-text)]">
                  Replying to {AGENT_LABELS[replyingTo.agentId]?.label || replyingTo.agentId}
                </div>
                <div class="text-[var(--color-text-muted)] truncate">{replyingTo.snippet}</div>
              </div>
              <button
                type="button"
                onClick={clearReply}
                class="text-[var(--color-text-muted)] hover:text-[var(--color-text)] shrink-0 mt-0.5"
                aria-label="Cancel reply"
              >
                <XIcon size={12} />
              </button>
            </div>
          )}
          <div class="flex items-end gap-2 relative">
            {/* Emoji picker popover — anchored above the input row */}
            {emojiOpen && (
              <div class="absolute bottom-12 left-0 z-10 bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-lg p-2 shadow-lg">
                <div class="grid grid-cols-10 gap-1 max-w-[280px]">
                  {EMOJI_GRID.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => { insertEmoji(e); setEmojiOpen(false); }}
                      class="text-lg hover:bg-[var(--color-card)] rounded p-1 leading-none"
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button
              type="button"
              onClick={() => setEmojiOpen((v) => !v)}
              title="Insert emoji"
              class="px-2 py-2 rounded-lg text-base bg-[var(--color-elevated)] border border-[var(--color-border)] hover:bg-[var(--color-card)] transition-colors"
            >
              😊
            </button>
            <textarea
              ref={inputRef}
              value={draft}
              onInput={(e) => setDraft((e.target as HTMLTextAreaElement).value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (draft.trim()) void send();
                }
              }}
              onPaste={onPasteImage}
              placeholder="Type a message. Shift+Enter for newline. Paste or attach an image."
              rows={1}
              class="flex-1 bg-[var(--color-elevated)] border border-[var(--color-border)] rounded-lg px-3 py-2 text-[13px] outline-none focus:border-[var(--color-accent)] resize-none max-h-32"
            />
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/gif"
              class="hidden"
              onChange={(e) => {
                const f = (e.target as HTMLInputElement).files?.[0];
                if (f) void uploadImage(f, draft.trim() || undefined);
                if (draft.trim()) setDraft('');
                (e.target as HTMLInputElement).value = '';
              }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingImage}
              title="Attach image (or paste a screenshot)"
              class="inline-flex items-center justify-center px-2 py-2 rounded-lg text-[12px] bg-[var(--color-elevated)] text-[var(--color-text)] border border-[var(--color-border)] hover:bg-[var(--color-card)] disabled:opacity-40 transition-colors"
            >
              <Paperclip size={14} />
            </button>
            {processing ? (
              <button
                type="button"
                onClick={abort}
                class="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-medium bg-[var(--color-status-failed)] text-white hover:opacity-90 transition-opacity"
              >
                <Square size={12} fill="currentColor" /> Stop
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => void send()}
                  disabled={!draft.trim() || sending}
                  class="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <Send size={12} /> {sending ? 'Sending…' : 'Send'}
                </button>
                {activeAgent === 'all' && (
                  <button
                    type="button"
                    onClick={() => void broadcast()}
                    disabled={!draft.trim() || sending}
                    title="Send this message to every sub-agent (research, comms, content, ops, code-builder, buyer-builder-comping). Each replies in its own thread; responses appear here as they arrive."
                    class="inline-flex items-center gap-1 px-3 py-2 rounded-lg text-[12px] font-medium bg-[var(--color-elevated)] text-[var(--color-text)] border border-[var(--color-border)] hover:bg-[var(--color-card)] disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    📢 Broadcast
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function SessionBar({
  contextPct, turnsToday, costToday, model, agentLabel,
}: {
  contextPct?: number; turnsToday: number; costToday: number; model?: string; agentLabel?: string;
}) {
  return (
    <div class="flex items-center gap-4 px-6 py-1.5 border-b border-[var(--color-border)] text-[10.5px] text-[var(--color-text-faint)] tabular-nums">
      {agentLabel && (
        <span><span class="uppercase tracking-wider">Agent</span> <span class="text-[var(--color-text-muted)] normal-case tracking-normal">{agentLabel}</span></span>
      )}
      {typeof contextPct === 'number' && (
        <span><span class="uppercase tracking-wider">Ctx</span> <span class="text-[var(--color-text-muted)]">{contextPct}%</span></span>
      )}
      <span><span class="uppercase tracking-wider">Turns today</span> <span class="text-[var(--color-text-muted)]">{formatNumber(turnsToday)}</span></span>
      {showCosts.value && (
        <span><span class="uppercase tracking-wider">Cost today</span> <span class="text-[var(--color-text-muted)]">{formatCost(costToday)}</span></span>
      )}
      {model && (
        <span class="ml-auto"><span class="uppercase tracking-wider">Model</span> <span class="text-[var(--color-text-muted)] normal-case font-mono">{model.replace('claude-', '')}</span></span>
      )}
    </div>
  );
}

function TabBtn({ label, active, onClick, live }: { label: string; active: boolean; onClick: () => void; live?: boolean }) {
  return (
    <button
      type="button"
      onClick={onClick}
      class={[
        'inline-flex items-center gap-1.5 px-3 py-1 rounded-md text-[12px] transition-colors',
        active
          ? 'bg-[var(--color-elevated)] text-[var(--color-text)]'
          : 'text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-elevated)]',
      ].join(' ')}
    >
      {live !== undefined && <StatusDot tone={live ? 'done' : 'cancelled'} />}
      {label}
    </button>
  );
}

// Per-agent label colors. Keeps each bot visually distinguishable in the All tab.
const AGENT_LABELS: Record<string, { label: string; color: string }> = {
  'main':                   { label: 'Dranos',   color: 'bg-blue-500/20 text-blue-300 border-blue-500/40' },
  'research':               { label: 'Research', color: 'bg-green-500/20 text-green-300 border-green-500/40' },
  'comms':                  { label: 'Comms',    color: 'bg-orange-500/20 text-orange-300 border-orange-500/40' },
  'content':                { label: 'Anna',     color: 'bg-purple-500/20 text-purple-300 border-purple-500/40' },
  'ops':                    { label: 'Ops',      color: 'bg-slate-500/20 text-slate-300 border-slate-500/40' },
  'buyer-builder-comping':  { label: 'BBC',      color: 'bg-rose-500/20 text-rose-300 border-rose-500/40' },
  'code-builder':           { label: 'CodeBld',  color: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/40' },
  'broadcast':              { label: 'Broadcast', color: 'bg-indigo-500/20 text-indigo-300 border-indigo-500/40' },
};

// Format a message timestamp for display under the bubble.
// Backend stores Unix seconds; client optimistic inserts use Date.now() (ms).
// Detect which by magnitude (anything under 1e12 is seconds).
function formatTimestamp(raw: number | undefined): string {
  if (!raw) return '';
  const ms = raw < 1e12 ? raw * 1000 : raw;
  const d = new Date(ms);
  if (Number.isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  const sameYear = d.getFullYear() === now.getFullYear();
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  if (sameDay) return time;
  const date = d.toLocaleDateString([], sameYear
    ? { month: 'short', day: 'numeric' }
    : { month: 'short', day: 'numeric', year: 'numeric' });
  return `${date} · ${time}`;
}

function Bubble({ turn, onReply }: { turn: Turn; onReply?: (agentId: string, content: string) => void }) {
  const isUser = turn.role === 'user';
  const isPhoto = !!turn.photoUrl;
  const html = (isUser || isPhoto) ? null : renderMarkdown(turn.content);
  const agentMeta = turn.agent_id ? AGENT_LABELS[turn.agent_id] : null;
  const stamp = formatTimestamp(turn.created_at);
  // Reply affordance: only on assistant messages from a known agent, only
  // when an onReply handler is provided (we don't show it in peer DM tabs).
  // Hidden by default; revealed on hover via group-hover Tailwind classes.
  const canReply = !isUser && !!turn.agent_id && !!onReply && !isPhoto;
  return (
    <div class={'flex group ' + (isUser ? 'justify-end' : 'justify-start')}>
      <div class="flex flex-col max-w-[75%]">
        {/* Agent label above the bubble — only on assistant messages, only when we can identify the bot */}
        {!isUser && agentMeta && (
          <div class={'self-start text-[9.5px] font-medium px-1.5 py-0.5 rounded mb-1 border ' + agentMeta.color}>
            {agentMeta.label}
          </div>
        )}
        <div class="relative">
          <div
            class={[
              'rounded-lg text-[12.5px] leading-relaxed overflow-hidden',
              isPhoto ? 'p-1' : 'px-3 py-2',
              isUser
                ? 'bg-[var(--color-accent)] text-white rounded-br-sm whitespace-pre-wrap'
                : 'bg-[var(--color-card)] border border-[var(--color-border)] text-[var(--color-text)] rounded-bl-sm chat-md',
            ].join(' ')}
          >
            {isPhoto ? (
              <>
                <img
                  src={turn.photoUrl}
                  alt={turn.photoCaption || 'attached image'}
                  class="block rounded max-h-[320px] w-auto object-contain"
                  loading="lazy"
                />
                {turn.photoCaption && (
                  <div class="px-2 py-1 text-[11px] text-[var(--color-text-muted)]">{turn.photoCaption}</div>
                )}
              </>
            ) : isUser ? (
              turn.content
            ) : (
              <div dangerouslySetInnerHTML={{ __html: html || '' }} />
            )}
            {turn.source === 'dashboard' && !isPhoto && (
              <div class="text-[9.5px] opacity-60 mt-1 uppercase tracking-wider">via dashboard</div>
            )}
          </div>
          {canReply && (
            <button
              type="button"
              onClick={() => onReply!(turn.agent_id!, turn.content)}
              title={`Reply directly to ${agentMeta?.label || turn.agent_id}`}
              class="absolute -right-2 -top-2 opacity-0 group-hover:opacity-100 transition-opacity bg-[var(--color-elevated)] border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] rounded-full p-1 shadow-sm"
              aria-label="Reply"
            >
              <CornerUpLeft size={11} />
            </button>
          )}
        </div>
        {stamp && (
          <div class={'text-[10px] text-[var(--color-text-muted)] mt-1 ' + (isUser ? 'self-end' : 'self-start')}>
            {stamp}
          </div>
        )}
      </div>
    </div>
  );
}

function ProcessingBubble({ label }: { label: string | null }) {
  return (
    <div class="flex justify-start">
      <div class="bg-[var(--color-card)] border border-[var(--color-border)] rounded-lg rounded-bl-sm px-3 py-2 text-[12px] text-[var(--color-text-muted)] inline-flex items-center gap-2">
        <Sparkles size={12} class="animate-pulse text-[var(--color-accent)]" />
        {label || 'Thinking…'}
      </div>
    </div>
  );
}
